// Asessment-form and Answers look alike however depending upon what tasks they perform, its separated from each other
// While assessmentForm is basically related to taking informations input
// Answer acts as a way to extract those submitted information

import { defineStore } from 'pinia';
import { ref } from 'vue';

import GlobalRequest from '@/api/global-request';
import useUserComposable from '@/composables/UserComposable';

import { showToast } from '@/helpers/toast-handler';
import {
    formatRequestDate,
    formatISODateToJSDate,
    formatMetricRequestDate,
} from '../../../../../helpers/date-formatter';
import { AttributeTypes } from '../../attribute/enum';
import { getPage, getPerPage } from '@/composables/PaginationComposable';

const ASSESSMENT_ANSWER_URL = 'v2/assessment/form';
const USER_PROFILE_ANSWER_URL = 'v2/user_profile/last';

export const useAnswerStore = defineStore('answer', () => {
    const { company_id, setUsers } = useUserComposable();
    // states
    const loading = ref(false);
    const sortParams = ref(null); // by default assessment submitted desc
    const criteria = ref({
        assessment_code: null,
        user_id: null,
        date_from: null,
        date_to: null,
        company_id: null,
        last: null,
        result: null,
        profile: null,
        page_size: null,
        page: null,
    });

    const answerParams = ref({
        assessment_code: null,
        form_id: null,
        company_id: company_id.value,
        output: 'raw', // by default raw. others are json and pdf
    });
    // to set all the submitted assessments
    const answers = ref([]);
    const totalRecords = ref(null);
    // to set a specific submitted assessment data
    const answer = ref(null);
    // to store the last submitted user profile answers and attributes details
    const userProfileAnswer = ref([]);

    // actions
    // this function will fetch all the answers i.e submitted assessments normal ones
    const getAllAnswers = async (params, list) => {
        try {

            let page_size = params?.page_size
            let page = params?.page

            if (typeof list !== 'undefined') {
                page_size = getPerPage(list)
                page = getPage(list) + 1
            }

            loading.value = true;
            const response = await GlobalRequest.getRequest(ASSESSMENT_ANSWER_URL, {
                params: {
                    assessment_code: params?.assessment_code,
                    user_id: params?.user_id,
                    date_from: formatMetricRequestDate(params?.date_from),
                    date_to: formatMetricRequestDate(params?.date_to),
                    company_id: company_id.value,
                    page_size: page_size,
                    page: page,
                    last: null,
                    result: params?.result,
                    profile: 'normal_only',
                    sort: sortParams.value,
                },
            });
            if (response.statusCode === 200) {
                answers.value = response.data.data;
                totalRecords.value = response.data.total;
            }
        } catch (error) {
            showToast({}, 'Error while fetching all answers');
        } finally {
            loading.value = false;
        }
    };

    // This function will extract the last submitted user profile answers as well as the latest user profile form attributes
    const getLatestUserProfileAnswer = async (userId) => {
        try {
            loading.value = true;
            const response = await GlobalRequest.getRequest(USER_PROFILE_ANSWER_URL, {
                params: {
                    user_id: userId,
                    company_id: company_id.value,
                },
            });
            if (response.statusCode === 200) {
                // Initially it will be in array even though the result might only be one or null as well
                userProfileAnswer.value = response.data?.answers?.map((item) => {
                    const attributeType = parseInt(item.attribute.type);
                    const formattedAnswer = formatAnswerByAttributeType(item.answer, attributeType);
                    return {
                        answer: formattedAnswer,
                        attribute_id: item.attribute?.id,
                        label: item.attribute?.label,
                        code: item.attribute?.code,
                        type: item.attribute?.type,
                    };
                });
            }
        } catch (error) {
            showToast({}, 'Error while fetching user profile answers');
        } finally {
            loading.value = false;
        }
    };

    const getUnassessedUsers = async (params) => {
        try {
            let page_size = params?.page_size || criteria.value.page_size;
            let page = params?.page;

            if (typeof params !== 'undefined' && params?.first !== undefined) {
                page_size = params.rows;
                page = (params.first / params.rows) + 1;
            }

            const response = await GlobalRequest.getRequest(`${ASSESSMENT_ANSWER_URL}/${criteria.value.assessment_code}/user`, {
                params: {
                    company_id: company_id.value,
                    assessment_code: criteria.value.assessment_code,
                    date_from: criteria.value.date_from ? formatRequestDate(criteria.value.date_from) : null,
                    date_to: criteria.value.date_to ? formatRequestDate(criteria.value.date_to) : null,
                    page_size: page_size,
                    page: page,
                },
            });
            if (response.statusCode === 200) {
                // set users data into the users store
                setUsers(response.data);
                // also return the data for direct use
                return response.data;
            }
            return { data: [], total: 0 };
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Error fetching untested users');
            return { data: [], total: 0 };
        }
    };

    const getAnswerByFormId = async (assessmentCode, formId) => {
        try {
            const response = await GlobalRequest.getRequest(`${ASSESSMENT_ANSWER_URL}/${assessmentCode}/${formId}`, {
                params: {
                    company_id: company_id.value,
                    output: 'json',
                },
            });
            if (response.statusCode === 200) {
                return response.data;
            }
            return null;
        } catch (error) {
            showToast({}, 'Failed to fetch information');
        }
    };

    const formatAnswerByAttributeType = (answer, type) => {
        switch (type) {
            case AttributeTypes.NUMBER:
            case AttributeTypes.DROP_DOWN:
            case AttributeTypes.RADIO:
                return parseInt(answer);

            case AttributeTypes.DATE:
                return formatISODateToJSDate(answer);

            case AttributeTypes.MULTI_DROP_DOWN:
            case AttributeTypes.CHECKBOX:
                return JSON.parse(answer);

            default:
                return answer;
        }
    };

    const deleteAssessmentForm = async(formId) => {
        try {
            //company_id: company_id.value,
            const response = await GlobalRequest.deleteRequest(`${ASSESSMENT_ANSWER_URL}/${formId}`, {
                params: {
                    company_id: company_id.value,
                }
                });
            if (response.statusCode === 200) {
                //await getAllAnswers();
                await showToast(response, 'Assessment deleted successfully');
            }
            return null;
        } catch (error) {
            await showToast({}, 'Failed to delete');
        }
    };

    // this function will fetch a particular assessment answer and download the file automatically
    const downloadAssessment = async (assessmentCode, formId) => {
        try {
            const response = await GlobalRequest.getRequest(`${ASSESSMENT_ANSWER_URL}/${assessmentCode}/${formId}?company_id=${company_id.value}&output='pdf'`, {
                responseType: 'blob',
            });
            // Create a blob URL from the response data
            const url = window.URL.createObjectURL(new Blob([response.data]));

            // Create a link element and set its attributes
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'downloaded_file.pdf');

            // Append the link to the document body and trigger a click event
            document.body.appendChild(link);
            link.click();
        } catch (error) {
            showToast({}, error.data.message ?? 'Failed to download document');
        }
    };

    // this function will clear all the existing criteria and set them to initial state
    const clearCriteria = async () => {
        criteria.value = {
            assessment_code: null,
            user_id: null,
            date_from: null,
            date_to: null,
            company_id: company_id.value,
        };
        // after clearing criteria, make sure to request for answers
        await getAllAnswers(criteria.value);
    };

    return {
        // states
        loading,
        sortParams,
        criteria,
        answerParams,
        answers,
        totalRecords,
        answer,
        userProfileAnswer,
        // getters

        // actions
        getAllAnswers,
        getLatestUserProfileAnswer,
        getUnassessedUsers,
        getAnswerByFormId,
        downloadAssessment,
        clearCriteria,
        deleteAssessmentForm,
    };
});
